/**
 * Get Single School API Contract
 *
 * GET /v1/schools/:school_public_id
 * Retrieve detailed information for a specific school.
 */

import { z } from 'zod'
import { SchoolStatusSchema } from '../../schemas/entities/school'

/**
 * Hostnames allowed to host school branding assets. The mobile and backend
 * sides validate URLs against this list to prevent arbitrary outbound fetches
 * if a config payload is compromised. Both Cloudinary CDN and the project's
 * S3 bucket-of-record are accepted during the transition window — see
 * `docs/brainstorms/2026-04-28-mobile-branding-assets-strategy-requirements.md`
 * (Open Q3) for context.
 */
export const BRANDING_URL_HOST_ALLOWLIST: ReadonlyArray<RegExp> = [
  /^https:\/\/res\.cloudinary\.com\//,
  /^https:\/\/[a-z0-9-]+\.s3\.[a-z0-9-]+\.amazonaws\.com\//,
  /^https:\/\/[a-z0-9-]+\.cloudfront\.net\//
]

const allowlistedAssetUrl = z
  .string()
  .url()
  .refine(value => BRANDING_URL_HOST_ALLOWLIST.some(re => re.test(value)), {
    message: 'URL host is not in the branding asset allowlist'
  })

/**
 * Single home-screen background asset. `urlDark` is reserved for v2; v1 mobile
 * consumes `url` regardless of theme and adapts via `bgConfig.overlay`.
 */
export const BackgroundAssetSchema = z
  .object({
    id: z.string(),
    url: allowlistedAssetUrl,
    urlDark: allowlistedAssetUrl.optional(),
    order: z.number().int().nonnegative(),
    category: z.string(),
    version: z.string().optional(),
    updatedAt: z.string().datetime().optional(),
    metadata: z.record(z.string(), z.unknown()).optional()
  })
  .passthrough()

export type BackgroundAsset = z.infer<typeof BackgroundAssetSchema>

/**
 * A logo asset with required light variant and optional dark variant. Mobile
 * falls back to `light` when `dark` is absent.
 */
export const LogoAssetSchema = z
  .object({
    light: allowlistedAssetUrl,
    dark: allowlistedAssetUrl.optional()
  })
  .passthrough()

export type LogoAsset = z.infer<typeof LogoAssetSchema>

export const AppConfigAssetsSchema = z
  .object({
    backgrounds: z
      .object({
        home: z.array(BackgroundAssetSchema).optional()
      })
      .passthrough()
      .optional(),
    logos: z
      .object({
        small: LogoAssetSchema.optional(),
        large: LogoAssetSchema.optional()
      })
      .passthrough()
      .optional()
  })
  .passthrough()

export type AppConfigAssets = z.infer<typeof AppConfigAssetsSchema>

/**
 * One referenced entry delivered inline with a school's app config (Custom
 * SVG Menu Icons plan, U5/KTD5). Deliberately NOT the full
 * `SchoolIconLibraryEntry` entity (`schemas/entities/school-icon-library.ts`)
 * — a device only ever needs to resolve a `custom-icon:<id>` reference
 * (`parseIconReference`) to paintable markup, so admin-only fields like
 * `name`/`sanitizerVersion`/timestamps stay off the wire here.
 */
export const AppConfigIconLibraryEntrySchema = z.object({
  /** SchoolIconLibraryEntry publicId — matches a reference's `entryPublicId`. */
  id: z.string(),
  /** Normalized, sanitized, single-color SVG markup — the delivered artifact (KTD18). */
  markup: z.string()
})

export type AppConfigIconLibraryEntry = z.infer<typeof AppConfigIconLibraryEntrySchema>

/**
 * Icon library slice of the delivered app config. Present only when the
 * capability is on AND the school's menus/pages reference at least one
 * library entry (KTD5, KTD16, AE12) — `entries` never carries the whole
 * library, only what was actually referenced, and a school that references
 * nothing gets no `iconLibrary` key at all (byte-identical to pre-feature
 * payloads).
 */
export const AppConfigIconLibrarySchema = z.object({
  entries: z.array(AppConfigIconLibraryEntrySchema)
})

export type AppConfigIconLibrary = z.infer<typeof AppConfigIconLibrarySchema>

/**
 * Permissive schema for the mobile-consumed `school.appConfig` payload.
 * Uses `.passthrough()` because mobile binaries persist in the field for
 * months and must safely ignore future-added fields. Only `assets` and
 * `iconLibrary` are typed here in Phase 1; remaining keys are deferred. The
 * richer `AppConfigSchema` in `schemas/entities/app-config.ts` covers other
 * keys (theme, screenFeatures, etc.) that flow through this same payload.
 */
export const SchoolAppConfigPayloadSchema = z
  .object({
    assets: AppConfigAssetsSchema.optional(),
    iconLibrary: AppConfigIconLibrarySchema.optional()
  })
  .passthrough()

export type SchoolAppConfigPayload = z.infer<typeof SchoolAppConfigPayloadSchema>

/**
 * Request Schema
 */
export const GetSingleSchoolRequestSchema = z.object({
  school_public_id: z.string().describe('School unique identifier'),
  subscription_plans: z.boolean().optional().default(true).describe('Include subscription plans'),
  app_config: z.boolean().optional().default(true).describe('Include app configuration'),
  reference_data: z.boolean().optional().default(true).describe('Include reference data'),
  sync_report: z.boolean().optional().default(false).describe('Include sync status report')
})

export type GetSingleSchoolRequest = z.infer<typeof GetSingleSchoolRequestSchema>

/**
 * School Detail Response Schema
 * Complete school information with nested configuration
 */
export const SchoolDetailSchema = z.object({
  id: z.string().describe('School unique identifier'),
  name: z.string().nullable().describe('School name'),
  slug: z.string().nullable().describe('School slug'),
  platform: z.string().nullable().describe('School platform'),
  environmentId: z.string().nullable().describe('Platform environment ID'),
  environmentName: z.string().nullable().describe('Platform environment name'),
  status: SchoolStatusSchema.describe('School status'),
  city: z.string().nullable().describe('School city'),
  state: z.string().nullable().describe('School state/province'),
  country: z.string().nullable().describe('School country'),
  // `School.latitude` / `.longitude` are Prisma `Float?`, and `GET /v1/schools/:id`
  // forwards them unchanged, so the wire carries numbers (SCH-3199). Note
  // `GET /v1/schools` stringifies its own copy — a separate contract.
  latitude: z.number().nullable().describe('School latitude'),
  longitude: z.number().nullable().describe('School longitude'),
  timezone: z.string().nullable().describe('School timezone'),
  totalUsers: z.number().optional().describe('Total number of users'),
  // Dynamic objects - structure varies by configuration
  subscriptionPlan: z.record(z.string(), z.unknown()).nullable().optional().describe('Subscription plan details'),
  referenceData: z.record(z.string(), z.unknown()).nullable().optional().describe('Reference data configuration'),
  appConfig: SchoolAppConfigPayloadSchema.nullable().optional().describe('App configuration'),
  syncReport: z.record(z.string(), z.unknown()).nullable().optional().describe('Sync status report'),
  createdAt: z.coerce.date().nullable().describe('Creation timestamp'),
  updatedAt: z.coerce.date().nullable().describe('Last update timestamp')
})

export type SchoolDetail = z.infer<typeof SchoolDetailSchema>

/**
 * Response Schema
 */
export const GetSingleSchoolResponseSchema = z.object({
  data: SchoolDetailSchema,
  message: z.string(),
  meta: z
    .object({
      setupError: z.record(z.string(), z.unknown()).optional().describe('Setup error details if present')
    })
    .optional()
})

export type GetSingleSchoolResponse = z.infer<typeof GetSingleSchoolResponseSchema>
